import React, { useState, useEffect } from 'react';
import { CalendarIcon, PeopleIcon, AnalyticIcon } from '../../assets/icons/icons';
import StatCard from '../../components/shared/StatsCard';
import Badge from '../../components/shared/Badge';
import { apiConnecter } from '../../services/apiConnector';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import SearchBox from '../../components/shared/SearchBox';
import { handleAnalyticsClick } from '../../components/shared/AnalyticNavigatefunc';
import API_ENDPOINTS from '../../constant/EndPoints';
import IconButton from '../../components/shared/IconButton';
import { getEngagementRate } from '../../services/getEngagementRate';
const MAX_HEIGHT_CALC = 'max-h-[calc(100vh-380px)]';

const Overview = () => {

    /* ****************************************************************
    *                            STATES                             *
    ******************************************************************/
    const userInfo = useSelector((state) => state.userInfo);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEvents, setFilteredEvents] = useState([]);
    const navigate = useNavigate();
    const [activeOrganizers, setActiveOrganizers] = useState(0);
    const [stateData, setStateData] = useState({ totalTaps: "0", totalEventsCount: "0", totalAttendees: "0", activeOrganizers: "0", totalEngagement: "0%", activeEvents: "0" })

    /* ****************************************************************
       *                            SERVICES                             *
       ******************************************************************/
    /**
     * Fetches recent event data from the API and updates the component's state with the relevant event details.
     * It also calculates total engagement, handles errors, and updates the UI with fetched results.
     * 
     * @returns {void}
     */
    const getAllEventsHandler = async () => {
        setLoading(true);
        try {
            const response = await apiConnecter(
                'GET',
                API_ENDPOINTS.REACT_APP_GET_ALL_RECENT_EVENTS_END_POINT,
                null,
                { authorization: `Bearer ${userInfo.token}` }
            );
            const activeOrganizersCount =
                console.log('get all events api response', response);
            const result = response.data.result;
            console.log("result", result)
            if (result.length === 0 || result.recentEvents.length === 0) {
                setFilteredEvents([]);
                setAllEvents([]);
                return;
            }
            const normalizedData = normalizedDatahandler(result.recentEvents);
            setAllEvents(normalizedData);
            setFilteredEvents(normalizedData);
            const totalEngagement = getEngagementRate(result.totalAttendees, result.totalTaps);

            setStateData((prev) => ({
                ...prev,
                totalTaps: result.totalTaps,
                totalEventsCount: result.totalEventsCount,
                totalAttendees: result.totalAttendees,
                activeOrganizers: result.totalActiveOrganizers,
                totalEngagement,
                activeEvents: result.totalActiveEvents
            }))


            // Calculate active organizers from the organizers array across all events

            // setActiveOrganizers(uniqueActiveOrganizers);
        } catch (err) {
            setError({ title: 'Error', message: err?.response?.data?.message ?? err.message });
        } finally {
            setLoading(false);
        }
    };


    /* ****************************************************************
    *                            HELPER FUNCTION                      *
    ******************************************************************/

    /**
 * Normalizes event data to a consistent format for easier processing.
 * Maps through the event array and returns a normalized object for each event with specific properties.
 * 
 * @param {Array} event - The array of event objects to normalize.
 * @param {Object} event._id - The unique identifier for the event.
 * @param {string} event.eventName - The name of the event.
 * @param {string} event.eventStartDate - The start date of the event.
 * @param {string} event.status - The current status of the event (e.g., "active", "completed").
 * @param {number} event.expectedAttendees - The expected number of attendees for the event.
 * @param {number} event.clickCount - The number of clicks (or interactions) for the event.
 * @param {string} event.location - The location of the event.
 * @param {string} event.baseUrl - The base URL for the bracelet (URL for attendee tracking or entry).
 * @param {string} event.destinationUrl - The URL for the event's destination (e.g., event page).
 * @param {number} event.postEventClickCount - The number of post-event clicks (engagement after the event).
 * 
 * @returns {Array} - The array of normalized event objects with properties:
 *  - id: A unique ID for the event.
 *  - event: The name of the event.
 *  - date: The formatted start date of the event.
 *  - status: The event's status.
 *  - attendance: The expected number of attendees.
 *  - taps: The number of taps or clicks related to the event.
 *  - engagement: The engagement percentage based on clicks and expected attendance.
 *  - location: The location of the event.
 *  - braceletUrl: The URL for the bracelet used for entry or tracking.
 *  - destinationUrl: The URL for the event's destination page.
 *  - postClick: The number of clicks after the event.
 */

    const normalizedDatahandler = (event) => {
        return event.map(event => ({
            id: event?._id || `event-${Math.random().toString(36).substr(2, 9)}`,
            event: event.eventName || 'Unnamed Event',
            date: event.eventStartDate
                ? new Date(event.eventStartDate).toISOString().split('T')[0]
                : 'N/A',
            status: event.status || 'Unknown',
            attendance: event.expectedAttendees || '0',
            taps: event.clickCount || '0',

            engagement: event?.expectedAttendees
                ? ((event.clickCount / event.expectedAttendees) * 100).toFixed(2)
                : '0.00',
            location: event.location || 'N/A',
            braceletUrl: event.baseUrl || 'https://bracelet.example.com/default',
            destinationUrl: event.destinationUrl || 'https://example.com/default',
            postClick: event?.postEventClickCount
        }));
    }

    /* ****************************************************************
      *                             EFFECT HOOKS                        *
      ******************************************************************/
    // Handle search functionality
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredEvents(allEvents);
        } else {
            const filtered = allEvents.filter(event =>
                event.event.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredEvents(filtered);
        }
    }, [searchTerm, allEvents]);


    useEffect(() => {
        getAllEventsHandler();
    }, []);

    return (
        <div className="text-[var(--primary-text-color)] m-4 w-full">
            <main>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="md:text-2xl font-semibold text-[var(--primary-text-color)]  text-xl ">Overview</h2>
                        <p className="text-[var(--color-grey)] text-sm mt-1">
                            Get a detailed summary of the event's performance and key metrics.
                        </p>
                    </div>

                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <StatCard
                        title="Total Events"
                        value={stateData.totalEventsCount}
                        icon={CalendarIcon}
                        description={`${stateData.activeEvents} active`}
                    />
                    <StatCard
                        title="Total Attendance"
                        value={stateData.totalAttendees}
                        icon={PeopleIcon}
                        description="Across all events"
                    />
                    <StatCard
                        title="Total Taps"
                        value={stateData.totalTaps}
                        icon={AnalyticIcon}
                        description={`${stateData.totalEngagement}% engagement rate`}
                    />
                    <StatCard
                        title="Active Organizers"
                        value={stateData.activeOrganizers}
                        icon={PeopleIcon}
                        description="Registered organizers"
                    />
                </div>

                {/* Recent Events */}
                <div className="space-y-4">
                    {/* Mobile / Tablet Cards */}
                    <div className="flex flex-col space-y-4 lg:hidden">
                        {loading ? (
                            // <div className="text-center text-[var(--secondary-text-color)]">Loading...</div>
                            <div className='flex items-center justify-center w-full p-2 bg-[var(--color-surface-background)]'>
                                <div className="loader"></div>
                            </div>
                        ) : error ? (
                            <div className="text-center text-red-500">{error.message}</div>
                        ) : filteredEvents.length === 0 ? (
                            <div className="text-center text-[var(--secondary-text-color)]">No events found</div>
                        ) : (
                            filteredEvents.map(event => (
                                <div
                                    key={event.id}
                                    className="bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-4 shadow-md flex flex-col space-y-2"
                                >
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold truncate">{event.event}</h3>
                                        <Badge
                                            label={event.status}
                                            bgColor={
                                                event.status === 'Active'
                                                    ? 'bg-[--color-primary-active]'
                                                    : event.status === 'Completed'
                                                        ? 'bg-[#262626]'
                                                        : 'bg-transparent'
                                            }
                                            textColor={event.status === 'Completed' ? 'text-white' : 'text-black'}
                                            borderColor={event.status === 'Upcoming' ? 'border-gray-700' : ''}
                                        />
                                    </div>

                                    <div className="text-[var(--secondary-text-color)] text-sm flex justify-between">
                                        <span>{event.date}</span>
                                        <span>{event.location}</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-[var(--primary-text-color)] text-sm">
                                        <div>
                                            <div className="text-[var(--secondary-text-color)] text-xs">Attendance</div>
                                            <div className="font-semibold">{event.attendance}</div>
                                        </div>
                                        <div>
                                            <div className="text-[var(--secondary-text-color)] text-xs">Taps</div>
                                            <div className="font-semibold">{event.taps}</div>
                                        </div>
                                        <div>
                                            <div className="text-[var(--secondary-text-color)] text-xs">Engagement</div>
                                            <div className="font-semibold">{event.engagement + "%"}</div>
                                        </div>
                                        <div>
                                            <div className="text-[var(--secondary-text-color)] text-xs">Actions</div>
                                            <button
                                                className="text-[var(--color-primary-dark)] text-green text-xs cursor-pointer"
                                                onClick={() =>
                                                    handleAnalyticsClick(event.id, event.taps, event.engagement, event?.postClick, event?.attendance, navigate)
                                                }
                                            >
                                                View Analytics
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex flex-col space-y-1 text-[var(--secondary-text-color)] text-xs mt-2 gap-2 flex-wrap">
                                        <span className='flex flex-wrap gap-2  items-center justify-start  xs:flex-col'>
                                            Bracelet URL:{' '}
                                            <a
                                                href={event.braceletUrl}
                                                target='_blank'
                                                className="bg-[var(--grey-button)] hover:bg-gray-600 text-[var(--secondary-text-color)] px-3 py-1 rounded-xl text-xs cursor-pointer"
                                            >
                                                {event?.braceletUrl?.slice(0, 50) + "..."}
                                            </a>
                                        </span>
                                        <span className='flex flex-wrap gap-2 items-center justify-start  xs:flex-col'>
                                            Destination URL:{' '}
                                            <a
                                                href={event.destinationUrl}
                                                target='_blank'
                                                className="bg-[var(--grey-button)] hover:bg-gray-600 text-[var(--secondary-text-color)] px-3 py-1 rounded-xl text-xs cursor-pointer"
                                            >
                                                {event.destinationUrl.slice(0, 50) + "..."}
                                            </a>
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop / Large Table */}
                    <div
                        className={`hidden lg:block bg-[var(--color-surface-background)] rounded-2xl border border-[var(--border-color)] p-5 overflow-x-auto max-h-[290px] custom-scrollbar ${MAX_HEIGHT_CALC} shadow-lg    `}
                    >
                        <h3 className="text-md text-[var(--primary-text-color)] mb-1">Recent Events</h3>
                        <p className="text-[var(--color-grey)] text-sm mb-3">
                            Latest SparkMotion events and their performance
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full table-fixed border-collapse">
                                <thead className="bg-[var(--color-surface-background)] sticky top-0 ">
                                    <tr className='border-b border-[var(--border-color)] hover:bg-gray-750"'>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/4">
                                            Event
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Date
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Status
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Attendance
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Taps
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Engagement
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-active)] w-1/6">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody className="overflow-y-auto">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="py-4 px-6 text-center text-black">
                                                <div className="loader"></div>
                                            </td>
                                        </tr>
                                    ) : error ? (
                                        <tr>
                                            <td colSpan="7" className="py-4 px-6 text-center text-red-500">
                                                {error.message}
                                            </td>
                                        </tr>
                                    ) : filteredEvents.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="py-4 px-6 text-center text-black">
                                                No events found
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredEvents.slice(0, 3).map(event => (
                                            <tr
                                                key={event.id}
                                                className="border-b border-[var(--border-color)] hover:bg-gray-750"
                                            >
                                                <td className="py-4 px-4 text-[var(--secondary-text-color)] font-medium">{event.event}</td>
                                                <td className="py-4 px-4 text-[var(--secondary-text-color)] ">{event.date}</td>
                                                <td className="py-4 px-4">
                                                    <Badge
                                                        label={event.status}
                                                        bgColor={
                                                            event.status === 'Active'
                                                                ? 'bg-[--color-primary-active]'
                                                                : event.status === 'Completed'
                                                                    ? 'bg-[#262626]'
                                                                    : 'bg-transparent'
                                                        }
                                                        textColor={event.status === 'Completed' ? 'text-[#FFFFFF]' : 'text-black'}
                                                        borderColor={event.status === 'Upcoming' ? 'border-gray-700' : ''}
                                                    />
                                                </td>
                                                <td className="py-4 px-4 text-[var(--secondary-text-color)] ">{event.attendance}</td>
                                                <td className="py-4 px-4 text-[var(--secondary-text-color)] ">{event.taps}</td>
                                                <td className="py-4 px-4 text-[var(--secondary-text-color)] ">{`${event.engagement}%`}</td>

                                                <td className="py-4 px-4 ">
                                                    <button
                                                        className="text-[var(--color-primary-dark)] hover:text-orange-800 text-sm cursor-pointer"
                                                        onClick={() =>
                                                            handleAnalyticsClick(event.id, event.taps, event.engagement, event?.postClick, event?.attendance, navigate)
                                                        }
                                                    >
                                                        View Analytics
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Overview;